import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, categoryApi, exchangeApi, uploadApi } from '../../api';
import Loading from '../../components/common/Loading';
import { toImageUrl } from '../../utils/image';

interface Translation { locale: string; name: string; shortDescription: string; }
interface RegionPrice { region: string; currency: string; price: string; taxIncluded: boolean; available: boolean; }
interface DescImage { url: string; displayOrder: number; main: boolean; }
interface VariantForm { name: string; stock: string; imageUrl: string; file: File | null; available: boolean; }

interface AdminProduct {
  id: number; code: string; categoryId: number; status: string; baseStock: number;
  weightGrams: number;
  thumbnailUrl: string | null;
  translations: { locale: string; name: string; shortDescription: string }[];
  regionPrices: { region: string; currency: string; price: number; taxIncluded: boolean; available: boolean }[];
  images: DescImage[];
  variants?: { id: number; name: string; imageUrl: string | null; stock: number; displayOrder: number; available: boolean }[];
}

const getName = (p: AdminProduct) =>
  p.translations.find(t => t.locale === 'KO')?.name ||
  p.translations.find(t => t.locale === 'EN')?.name || p.code;

const REGIONS = [
  { region: 'US', currency: 'USD' },
];

const LOCALE_FLAGS: Record<string, string> = { KO: '🇰🇷 한국어', EN: '🇺🇸 English' };
const REGION_LABELS: Record<string, string> = { US: '🇺🇸 US (USD)' };
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'ACTIVE', label: '판매중' },
  { value: 'DRAFT', label: '임시저장' },
  { value: 'HIDDEN', label: '숨김' },
  { value: 'SOLD_OUT', label: '품절' },
];

const emptyTranslations = (): Translation[] => [
  { locale: 'KO', name: '', shortDescription: '' },
  { locale: 'EN', name: '', shortDescription: '' },
];

const emptyRegionPrices = (): RegionPrice[] => REGIONS.map(r => ({
  ...r, price: '', taxIncluded: true, available: true,
}));

interface FormState {
  code: string; categoryId: string; status: string; baseStock: string; weightGrams: string;
  translations: Translation[]; regionPrices: RegionPrice[]; variants: VariantForm[];
}

const EMPTY_FORM: FormState = {
  code: '', categoryId: '', status: 'ACTIVE', baseStock: '0', weightGrams: '300',
  translations: emptyTranslations(), regionPrices: emptyRegionPrices(), variants: [],
};

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState<AdminProduct | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [krwPerUsd, setKrwPerUsd] = useState<number>(1300);

  // 썸네일
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [thumbPreview, setThumbPreview] = useState<string>('');
  const thumbInputRef = useRef<HTMLInputElement>(null);

  // 설명 이미지
  const [descImages, setDescImages] = useState<DescImage[]>([]);
  const [descFiles, setDescFiles] = useState<(File | null)[]>([]);
  const descInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    exchangeApi.get().then(r => setKrwPerUsd(r.data.data.krwPerUsd)).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.getProducts(page, 20), categoryApi.getAll()])
      .then(([p, c]) => {
        setProducts(p.data.data.content as unknown as AdminProduct[]);
        setTotalPages(p.data.data.totalPages);
        const flat: { id: number; name: string }[] = [];
        const walk = (cats: any[]) => cats.forEach(cat => {
          flat.push({ id: cat.id, name: cat.name });
          if (cat.children) walk(cat.children);
        });
        walk(c.data.data as any[]);
        setCategories(flat);
      }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const resetImageState = () => {
    setThumbFile(null);
    setThumbPreview('');
    setDescImages([]);
    setDescFiles([]);
  };

  const openCreate = () => {
    setEditProduct(null);
    setForm({ ...EMPTY_FORM, translations: emptyTranslations(), regionPrices: emptyRegionPrices(), variants: [] });
    resetImageState();
    setShowForm(true);
  };

  const openEdit = (p: AdminProduct) => {
    setEditProduct(p);
    const trans = emptyTranslations().map(t => {
      const found = p.translations.find(pt => pt.locale === t.locale);
      return found ? { locale: t.locale, name: found.name, shortDescription: found.shortDescription || '' } : t;
    });
    const prices = emptyRegionPrices().map(r => {
      const found = p.regionPrices.find(pr => pr.region === r.region);
      return found ? { ...r, price: String(found.price), taxIncluded: found.taxIncluded, available: found.available } : r;
    });
    const variants: VariantForm[] = [...(p.variants || [])]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(v => ({ name: v.name, stock: String(v.stock), imageUrl: v.imageUrl || '', file: null, available: v.available }));
    setForm({ code: p.code, categoryId: String(p.categoryId), status: p.status, baseStock: String(p.baseStock), weightGrams: String(p.weightGrams ?? 300), translations: trans, regionPrices: prices, variants });

    // 기존 썸네일
    setThumbFile(null);
    setThumbPreview(p.thumbnailUrl ? toImageUrl(p.thumbnailUrl) : '');

    // 기존 설명 이미지
    const sorted = [...(p.images || [])].sort((a, b) => a.displayOrder - b.displayOrder);
    setDescImages(sorted);
    setDescFiles(sorted.map(() => null));

    setShowForm(true);
  };

  const onThumbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbFile(file);
    setThumbPreview(URL.createObjectURL(file));
  };

  const onDescFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newImages: DescImage[] = files.map((_, i) => ({
      url: '', displayOrder: descImages.length + i + 1, main: false,
    }));
    const newFiles: (File | null)[] = files;
    setDescImages(prev => [...prev, ...newImages]);
    setDescFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeDescImage = (idx: number) => {
    setDescImages(prev => prev.filter((_, i) => i !== idx).map((img, i) => ({ ...img, displayOrder: i + 1 })));
    setDescFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const moveDescImage = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= descImages.length) return;
    setDescImages(prev => {
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr.map((img, i) => ({ ...img, displayOrder: i + 1 }));
    });
    setDescFiles(prev => {
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const setTrans = (i: number, key: keyof Translation, val: string) =>
    setForm(f => { const t = [...f.translations]; t[i] = { ...t[i], [key]: val }; return { ...f, translations: t }; });

  const setPrice = (i: number, key: keyof RegionPrice, val: any) =>
    setForm(f => { const r = [...f.regionPrices]; r[i] = { ...r[i], [key]: val }; return { ...f, regionPrices: r }; });

  const addVariant = () =>
    setForm(f => ({ ...f, variants: [...f.variants, { name: '', stock: '0', imageUrl: '', file: null, available: true }] }));
  const removeVariant = (i: number) =>
    setForm(f => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
  const setVariant = (i: number, key: keyof VariantForm, val: any) =>
    setForm(f => { const v = [...f.variants]; v[i] = { ...v[i], [key]: val }; return { ...f, variants: v }; });
  const onVariantImage = (i: number, file: File | null) =>
    setForm(f => { const v = [...f.variants]; v[i] = { ...v[i], file, imageUrl: file ? '' : v[i].imageUrl }; return { ...f, variants: v }; });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // 썸네일 업로드
      let thumbnailUrl: string | undefined = thumbPreview && !thumbFile
        ? (editProduct?.thumbnailUrl || undefined)
        : undefined;
      if (thumbFile) {
        const res = await uploadApi.image(thumbFile);
        thumbnailUrl = res.data.data;
      }

      // 설명 이미지 업로드
      const uploadedDescImages: { url: string; displayOrder: number; main: boolean }[] = [];
      for (let i = 0; i < descImages.length; i++) {
        const file = descFiles[i];
        if (file) {
          const res = await uploadApi.image(file);
          uploadedDescImages.push({ url: res.data.data, displayOrder: i + 1, main: i === 0 });
        } else if (descImages[i].url) {
          uploadedDescImages.push({ url: descImages[i].url, displayOrder: i + 1, main: i === 0 });
        }
      }

      // 옵션(배리언트) 이미지 업로드 + payload 구성
      const variantsPayload: { name: string; imageUrl?: string; stock: number; displayOrder: number; available: boolean }[] = [];
      for (let i = 0; i < form.variants.length; i++) {
        const v = form.variants[i];
        if (!v.name.trim()) continue;
        let imageUrl = v.imageUrl || undefined;
        if (v.file) {
          const res = await uploadApi.image(v.file);
          imageUrl = res.data.data;
        }
        variantsPayload.push({ name: v.name.trim(), imageUrl, stock: Number(v.stock || 0), displayOrder: i + 1, available: v.available });
      }

      const payload = {
        code: form.code,
        categoryId: Number(form.categoryId),
        status: form.status,
        baseStock: Number(form.baseStock),
        weightGrams: Number(form.weightGrams || 0),
        thumbnailUrl: thumbnailUrl || undefined,
        translations: form.translations
          .filter(t => t.name.trim())
          .map(t => ({ locale: t.locale, name: t.name, shortDescription: t.shortDescription || undefined })),
        regionPrices: form.regionPrices
          .filter(r => r.price)
          .map(r => ({ region: r.region, currency: r.currency, price: Number(r.price), taxIncluded: r.taxIncluded, available: r.available })),
        images: uploadedDescImages.length > 0 ? uploadedDescImages : undefined,
        variants: variantsPayload,
      };

      if (editProduct) {
        await (adminApi.updateProduct as any)(editProduct.id, payload);
      } else {
        await (adminApi.createProduct as any)(payload);
      }
      setShowForm(false);
      resetImageState();
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.title}>상품 관리</h2>
        <div style={s.headerRight}>
          <div style={s.rateBox}>1 USD ≈ <strong>{krwPerUsd.toLocaleString()}</strong> KRW</div>
          <button style={s.addBtn} onClick={openCreate}>+ 상품 등록</button>
        </div>
      </div>

      {loading ? <Loading /> : (
        <>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>{['ID', '썸네일', '코드 / 상품명', '카테고리', '상태', '재고', '무게', '가격 (USD)', '한화 환산', '관리'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const usPrice = p.regionPrices.find(r => r.region === 'US');
                  const cat = categories.find(c => c.id === p.categoryId);
                  return (
                    <tr key={p.id} style={s.tr}>
                      <td style={s.td}>{p.id}</td>
                      <td style={s.td}>
                        {p.thumbnailUrl
                          ? <img src={toImageUrl(p.thumbnailUrl)} alt="" style={s.thumb} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          : <div style={s.noThumb}>-</div>}
                      </td>
                      <td style={s.td}>
                        <Link to={`/products/${p.id}`} target="_blank" rel="noopener noreferrer"
                          style={{ fontWeight: 600, fontSize: 13, color: '#333', textDecoration: 'none' }}
                          onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                          {getName(p)}
                        </Link>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{p.code}</div>
                      </td>
                      <td style={s.td}>{cat?.name || p.categoryId}</td>
                      <td style={s.td}>
                        <span style={{ ...s.statusBadge, background: p.status === 'ACTIVE' ? '#e8f5e9' : '#f5f5f5', color: p.status === 'ACTIVE' ? '#4caf50' : '#999' }}>
                          {STATUS_OPTIONS.find(o => o.value === p.status)?.label || p.status}
                        </span>
                      </td>
                      <td style={s.td}>{p.baseStock}</td>
                      <td style={s.td}>{p.weightGrams != null ? `${p.weightGrams.toLocaleString()}g` : '-'}</td>
                      <td style={s.td}>{usPrice ? `$${Number(usPrice.price).toLocaleString()}` : '-'}</td>
                      <td style={s.td}>
                        {usPrice
                          ? <span style={{ color: '#888', fontSize: 12 }}>≈ {Math.round(Number(usPrice.price) * krwPerUsd).toLocaleString()}원</span>
                          : '-'}
                      </td>
                      <td style={s.td}>
                        <div style={s.actions}>
                          <button style={s.editBtn} onClick={() => openEdit(p)}>수정</button>
                          <button style={s.delBtn} onClick={async () => { if (window.confirm('삭제?')) { await adminApi.deleteProduct(p.id); load(); } }}>삭제</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={s.pagination}>
            <button style={s.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>← 이전</button>
            <span style={s.pageInfo}>{page + 1} / {totalPages || 1}</span>
            <button style={s.pageBtn} disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>다음 →</button>
          </div>
        </>
      )}

      {showForm && (
        <div style={s.overlay}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{editProduct ? '상품 수정' : '상품 등록'}</h3>
            <form onSubmit={handleSubmit} style={s.form}>

              {/* 기본 정보 */}
              <div style={s.section}>
                <div style={s.sectionTitle}>기본 정보</div>
                <div style={s.grid2}>
                  <div style={s.field}>
                    <label style={s.label}>코드 *</label>
                    <input style={s.input} required value={form.code}
                      onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                      placeholder="예: green-tea-serum-50ml" />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>카테고리 *</label>
                    <select style={s.input} required value={form.categoryId}
                      onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}>
                      <option value="">선택</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>상태</label>
                    <select style={s.input} value={form.status}
                      onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>재고</label>
                    <input style={s.input} type="number" min="0" value={form.baseStock}
                      onChange={e => setForm(f => ({ ...f, baseStock: e.target.value }))} />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>무게 (g) *</label>
                    <input style={s.input} type="number" min="0" required value={form.weightGrams}
                      onChange={e => setForm(f => ({ ...f, weightGrams: e.target.value }))}
                      placeholder="예: 300" />
                    {form.weightGrams && Number(form.weightGrams) > 0 && (
                      <span style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        ≈ {(Number(form.weightGrams) / 1000).toLocaleString(undefined, { maximumFractionDigits: 3 })} kg · 배송비는 무게 합산 구간으로 자동 계산
                      </span>
                    )}
                  </div>
                </div>

                {/* 썸네일 업로드 */}
                <div style={s.field}>
                  <label style={s.label}>썸네일 이미지</label>
                  <div style={s.thumbRow}>
                    {thumbPreview && (
                      <img src={thumbPreview} alt="썸네일" style={s.thumbPreview}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    )}
                    <div style={s.thumbActions}>
                      <button type="button" style={s.uploadBtn} onClick={() => thumbInputRef.current?.click()}>
                        {thumbPreview ? '이미지 변경' : '이미지 선택'}
                      </button>
                      {thumbPreview && (
                        <button type="button" style={s.removeThumbBtn} onClick={() => { setThumbFile(null); setThumbPreview(''); }}>
                          제거
                        </button>
                      )}
                    </div>
                  </div>
                  <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onThumbChange} />
                </div>
              </div>

              {/* 다국어 정보 */}
              <div style={s.section}>
                <div style={s.sectionTitle}>다국어 정보</div>
                {form.translations.map((t, i) => (
                  <div key={t.locale} style={s.langBlock}>
                    <div style={s.langLabel}>{LOCALE_FLAGS[t.locale]}</div>
                    <div style={s.grid2}>
                      <div style={s.field}>
                        <label style={s.label}>상품명{t.locale === 'KO' ? ' *' : ''}</label>
                        <input style={s.input} required={t.locale === 'KO'} value={t.name}
                          onChange={e => setTrans(i, 'name', e.target.value)} />
                      </div>
                      <div style={s.field}>
                        <label style={s.label}>짧은 설명</label>
                        <input style={s.input} value={t.shortDescription}
                          onChange={e => setTrans(i, 'shortDescription', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 지역별 가격 */}
              <div style={s.section}>
                <div style={s.sectionTitle}>지역별 가격</div>
                {form.regionPrices.map((r, i) => (
                  <div key={r.region} style={s.langBlock}>
                    <div style={s.langLabel}>{REGION_LABELS[r.region]}</div>
                    <div style={s.grid2}>
                      <div style={s.field}>
                        <label style={s.label}>가격 ({r.currency})</label>
                        <input style={s.input} type="number" min="0" step="0.01" value={r.price}
                          onChange={e => setPrice(i, 'price', e.target.value)} />
                        {r.price && Number(r.price) > 0 && (
                          <span style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            ≈ {Math.round(Number(r.price) * krwPerUsd).toLocaleString()}원
                          </span>
                        )}
                      </div>
                      <div style={s.field}>
                        <label style={s.label}>설정</label>
                        <div style={{ display: 'flex', gap: 12, paddingTop: 8 }}>
                          <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input type="checkbox" checked={r.taxIncluded} onChange={e => setPrice(i, 'taxIncluded', e.target.checked)} />
                            세금포함
                          </label>
                          <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input type="checkbox" checked={r.available} onChange={e => setPrice(i, 'available', e.target.checked)} />
                            판매가능
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 디자인 옵션 */}
              <div style={s.section}>
                <div style={s.sectionTitle}>디자인 옵션 (선택) — 같은 상품군에서 디자인만 다를 때. 가격·무게는 공통, 재고만 옵션별.</div>
                {form.variants.map((v, i) => {
                  const preview = v.file ? URL.createObjectURL(v.file) : (v.imageUrl ? toImageUrl(v.imageUrl) : '');
                  return (
                    <div key={i} style={s.variantRow}>
                      {preview
                        ? <img src={preview} alt="" style={s.variantThumb} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        : <div style={s.variantNoThumb}>-</div>}
                      <input style={{ ...s.input, flex: 1 }} placeholder="디자인명 (예: Red)" value={v.name}
                        onChange={e => setVariant(i, 'name', e.target.value)} />
                      <input style={{ ...s.input, width: 80 }} type="number" min="0" placeholder="재고" value={v.stock}
                        onChange={e => setVariant(i, 'stock', e.target.value)} />
                      <label style={s.variantCheck}>
                        <input type="checkbox" checked={v.available} onChange={e => setVariant(i, 'available', e.target.checked)} />
                        판매
                      </label>
                      <label style={s.variantUploadBtn}>
                        {v.file || v.imageUrl ? '변경' : '이미지'}
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => onVariantImage(i, e.target.files?.[0] ?? null)} />
                      </label>
                      <button type="button" style={s.variantRemove} onClick={() => removeVariant(i)}>✕</button>
                    </div>
                  );
                })}
                <button type="button" style={s.uploadBtn} onClick={addVariant}>+ 옵션 추가</button>
              </div>

              {/* 설명 이미지 */}
              <div style={s.section}>
                <div style={s.sectionTitle}>상품 설명 이미지 (순서대로)</div>
                <div style={s.descImgGrid}>
                  {descImages.map((img, idx) => {
                    const previewSrc = descFiles[idx]
                      ? URL.createObjectURL(descFiles[idx]!)
                      : (img.url ? toImageUrl(img.url) : '');
                    return (
                      <div key={idx} style={s.descImgCard}>
                        <div style={s.descImgOrder}>{idx + 1}</div>
                        {previewSrc && (
                          <img src={previewSrc} alt="" style={s.descImgThumb}
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        <div style={s.descImgActions}>
                          <button type="button" style={s.descMoveBtn} onClick={() => moveDescImage(idx, -1)} disabled={idx === 0}>↑</button>
                          <button type="button" style={s.descMoveBtn} onClick={() => moveDescImage(idx, 1)} disabled={idx === descImages.length - 1}>↓</button>
                          <button type="button" style={s.descRemoveBtn} onClick={() => removeDescImage(idx)}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button type="button" style={s.uploadBtn} onClick={() => descInputRef.current?.click()}>
                  + 이미지 추가
                </button>
                <input ref={descInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onDescFilesChange} />
              </div>

              <div style={s.modalBtns}>
                <button type="button" style={s.cancelBtn} onClick={() => { setShowForm(false); resetImageState(); }}>취소</button>
                <button type="submit" style={s.saveBtn} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700 },
  headerRight: { display: 'flex', gap: 12, alignItems: 'center' },
  rateBox: { fontSize: 12, color: '#888', background: '#f5f5f5', padding: '6px 12px', borderRadius: 6 },
  addBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  tableWrap: { background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '12px 14px', textAlign: 'left', color: '#888', fontWeight: 600, borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f9f9f9' },
  td: { padding: '12px 14px', color: '#444', verticalAlign: 'middle' },
  thumb: { width: 44, height: 44, objectFit: 'cover', borderRadius: 6 },
  noThumb: { width: 44, height: 44, background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 11 },
  statusBadge: { fontSize: 11, padding: '3px 8px', borderRadius: 10, fontWeight: 600 },
  actions: { display: 'flex', gap: 6 },
  editBtn: { padding: '4px 10px', background: '#ece3f7', color: '#b39ddb', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  delBtn: { padding: '4px 10px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 20 },
  pageBtn: { padding: '8px 16px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13 },
  pageInfo: { fontSize: 13, color: '#888' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 24 },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  section: { background: '#fafafa', borderRadius: 10, padding: 16, border: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 14 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#333' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
  langBlock: { borderTop: '1px solid #f0f0f0', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  langLabel: { fontSize: 12, fontWeight: 700, color: '#555' },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 500, color: '#666' },
  input: { padding: '10px 12px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 13, outline: 'none' },
  thumbRow: { display: 'flex', alignItems: 'center', gap: 16 },
  thumbPreview: { width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #e8e8e8' },
  thumbActions: { display: 'flex', flexDirection: 'column', gap: 8 },
  uploadBtn: { padding: '8px 16px', background: '#f5f5f5', color: '#555', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 12, cursor: 'pointer' },
  removeThumbBtn: { padding: '6px 12px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' },
  variantRow: { display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #f0f0f0', paddingTop: 10 },
  variantThumb: { width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 },
  variantNoThumb: { width: 40, height: 40, background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 11, flexShrink: 0 },
  variantCheck: { fontSize: 12, display: 'flex', gap: 4, alignItems: 'center', whiteSpace: 'nowrap' },
  variantUploadBtn: { padding: '8px 12px', background: '#f5f5f5', color: '#555', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  variantRemove: { padding: '6px 10px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  descImgGrid: { display: 'flex', flexWrap: 'wrap', gap: 12 },
  descImgCard: { border: '1px solid #e8e8e8', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 110 },
  descImgOrder: { fontSize: 11, fontWeight: 700, color: '#b39ddb' },
  descImgThumb: { width: 90, height: 90, objectFit: 'cover', borderRadius: 6 },
  descImgActions: { display: 'flex', gap: 4 },
  descMoveBtn: { padding: '3px 7px', background: '#f5f5f5', border: '1px solid #e8e8e8', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  descRemoveBtn: { padding: '3px 7px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  modalBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 },
  cancelBtn: { padding: '10px 20px', border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 },
  saveBtn: { padding: '10px 24px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};

export default AdminProducts;
